// 3D tilt for an achievement card: it tilts toward a mouse pointer and, when
// `idle` is set, floats on its own the rest of the time. The glare, the foil,
// and the shadow move against the tilt, which sells the depth. Render the
// light with CardLight; the element that tilts needs a perspective ancestor.

import React, { useRef } from 'react';
import {
  useAnimationFrame,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from 'framer-motion';

// Largest tilt in degrees, around each axis.
const MAX_TILT_X = 14;
const MAX_TILT_Y = 20;

export function useCardTilt({ idle }: { idle: boolean }) {
  const reduceMotion = useReducedMotion();
  // Tilt targets. The springs follow them, so a change of target (the pointer
  // arriving or leaving) never jumps.
  const targetX = useMotionValue(0);
  const targetY = useMotionValue(0);
  const rotateX = useSpring(targetX, { stiffness: 140, damping: 16 });
  const rotateY = useSpring(targetY, { stiffness: 140, damping: 16 });
  const pointerActive = useRef(false);

  // Idle float: two slow waves with different periods, so the turn never
  // visibly repeats.
  useAnimationFrame((time) => {
    if (!idle || reduceMotion || pointerActive.current) return;
    targetY.set(13 * Math.sin(time / 1700));
    targetX.set(6 * Math.sin(time / 1250 + 1));
  });

  const glareX = useTransform(rotateY, [-MAX_TILT_Y, MAX_TILT_Y], [90, 10]);
  const glareY = useTransform(rotateX, [-MAX_TILT_X, MAX_TILT_X], [10, 90]);
  const glare = useMotionTemplate`radial-gradient(circle at ${glareX}% ${glareY}%, rgb(255 255 255 / 0.7), transparent 50%)`;
  const foilX = useTransform(rotateY, [-MAX_TILT_Y, MAX_TILT_Y], [0, 100]);
  const foilPosition = useMotionTemplate`${foilX}% 50%`;
  const shadowX = useTransform(rotateY, (deg) => -deg * 1.1);
  const shadowY = useTransform(rotateX, (deg) => 22 + deg * 1.1);
  const shadow = useMotionTemplate`${shadowX}px ${shadowY}px 36px -10px rgb(0 0 0 / 0.4)`;

  /** For the wrapper around the tilting element; it must not tilt itself. */
  const pointerHandlers = {
    onPointerMove: (event: React.PointerEvent<HTMLElement>) => {
      if (reduceMotion || event.pointerType !== 'mouse') return;
      const box = event.currentTarget.getBoundingClientRect();
      const x = (event.clientX - box.left) / box.width - 0.5;
      const y = (event.clientY - box.top) / box.height - 0.5;
      pointerActive.current = true;
      targetY.set(x * 2 * MAX_TILT_Y);
      targetX.set(-y * 2 * MAX_TILT_X);
    },
    onPointerLeave: () => {
      pointerActive.current = false;
      if (!idle) rest();
    },
  };

  /** Settles the card flat, e.g. while the pointer is over a control on it. */
  function rest() {
    targetX.set(0);
    targetY.set(0);
  }

  return {
    reduceMotion,
    rotateX,
    rotateY,
    glare,
    foilPosition,
    shadow,
    pointerHandlers,
    rest,
  };
}
